/**
 * In-flight request deduplication keyed by `K`.
 *
 * Problem this solves: two callers race on a per-key store cache —
 * both check, both miss, both fire the network request. Classic
 * stampede. With this map, the second caller sees a pending promise
 * for the same key and joins it instead of firing.
 *
 * Two consumption shapes:
 *
 * 1. **Singular** — caller knows the fetch is for one key:
 *    ```ts
 *    return inFlight.runOnce(id, () => fetchOne(id));
 *    ```
 *    The factory is invoked at most once per concurrent burst per key.
 *
 * 2. **Bulk** — one network call resolves N keys at once:
 *    ```ts
 *    const resolvers = chunk.map((id) => ({ id, resolve: inFlight.register(id) }));
 *    const res = await fetchBulk(chunk);
 *    for (const { id, resolve } of resolvers) resolve(extractFor(id, res));
 *    ```
 *    A concurrent caller asking about any registered id sees the
 *    pending promise via `get(id)` and joins.
 *
 * Promises auto-clear from the map on settle, so re-fetches after
 * resolution go through the network as expected.
 *
 * Used by `useFireAlertsService` + `useDeforestationService` to dedupe the
 * `manager.composable.getLayers` vs `alert-indicator.loadAlerts`
 * stampede on zone-open. Same pattern applies to any service whose
 * callers race on a per-id cache.
 */
export class InFlightMap<K, V> {
  private readonly pending = new Map<K, Promise<V>>();

  public has(key: K): boolean {
    return this.pending.has(key);
  }

  public get(key: K): Promise<V> | undefined {
    return this.pending.get(key);
  }

  /**
   * Run `factory` at most once per concurrent burst for `key`.
   * Subsequent calls during the in-flight window return the same
   * promise. After settle (resolve or reject), the slot clears so
   * future calls fetch again.
   */
  public async runOnce(key: K, factory: () => Promise<V>): Promise<V> {
    const pending = this.pending.get(key);
    if (pending) {
      return pending;
    }
    const promise = factory();
    this.pending.set(key, promise);
    try {
      return await promise;
    } finally {
      this.pending.delete(key);
    }
  }

  /**
   * Register a manually-resolvable pending slot. Caller must call the
   * returned function with the resolved value, after which the slot
   * clears. If the caller fails to resolve (rare), the slot stays —
   * use `clear(key)` from a finally block to recover.
   */
  public register(key: K): (value: V) => void {
    let resolver!: (value: V) => void;
    const promise = new Promise<V>((resolve) => {
      resolver = resolve;
    });
    this.pending.set(key, promise);
    promise.finally(() => this.pending.delete(key));
    return resolver;
  }

  /** Drop the slot for `key` without resolving. Use in finally blocks of bulk fetches that may throw. */
  public clear(key: K): void {
    this.pending.delete(key);
  }
}

/**
 * Single-cell variant of `InFlightMap` — same dedup semantics for a
 * resource that has no key (e.g. one global `/radd/layer` URL, one
 * static Hansen tile-template). Concurrent callers join the same
 * promise; settle clears the cell so a future call re-fetches.
 *
 * Pair with a store cache for the "fetched-once-this-session" case:
 *
 * ```ts
 * if (this.store.layer) return this.store.layer;
 * return this.cell.runOnce(async () => {
 *   const res = await this.api.get<T>(url);
 *   if (res.isSuccess && res.data) this.store.setLayer(res);
 *   return res;
 * });
 * ```
 *
 * Without the cell, two parallel callers both miss the store, both
 * fetch — same stampede pattern InFlightMap solves for keyed lookups.
 */
export class InFlightCell<V> {
  private pending: Promise<V> | null = null;

  public async runOnce(factory: () => Promise<V>): Promise<V> {
    if (this.pending) {
      return this.pending;
    }
    this.pending = factory();
    try {
      return await this.pending;
    } finally {
      this.pending = null;
    }
  }
}
